/**
 * Pictures, files and screenshots on a deal's chat.
 *
 * air-1792425662527-bfc430, from David: "It would be great to
 * insert/paste pictures/files/screenshots into the chat/communications
 * area." Paste is named first and is the one that matters — the thing
 * people actually do is screenshot a plat or a title exception and
 * want it in the thread, not saved to Downloads and picked back out.
 *
 * ── Why this module exists at all ─────────────────────────────────────
 *
 * The storage path is a SECURITY BOUNDARY, and it has to be built and
 * checked by the same code or the two drift.
 *
 * api/deals/messages.js runs as the service role with RLS switched off.
 * The browser uploads the file itself (straight to storage, where the
 * bucket's own policy admits only tenants/<your tenant>/...), and then
 * tells the route which path to attach. If the route trusted that
 * path, a caller could name ANY object in the bucket — a W-9, a
 * settlement statement, another deal's deed — attach it to a message
 * on a deal they can edit, and read it back through the attachment.
 * The upload policy would not stop them: they are not uploading
 * anything, they are pointing at something already there.
 *
 * So chatAttachmentPath() builds it, parseChatAttachmentPath() takes it
 * apart, and the route rebuilds the expected path from the caller's OWN
 * tenant and the deal it has already authorised, then compares. Any
 * mismatch is refused. Both sides import this file.
 */

/** Same ceiling as a document upload — 25 MB. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** At most this many on one message. A paste of twelve is a mistake. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 10;

/**
 * What may be attached.
 *
 * The same set a document upload accepts, which is already "pictures,
 * files and screenshots": PDF, the image formats a screenshot or a
 * phone photo arrives in, Word, Excel, and plain text. Deliberately
 * NOT a general file dump — no archives, no executables, nothing the
 * browser would be asked to run.
 */
export const ALLOWED_ATTACHMENT_MIME = Object.freeze(new Set([
  'application/pdf',
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/csv',
]));

/** The ones worth showing rather than listing. */
export function isImageAttachment(mime) {
  const type = String(mime || '').toLowerCase();
  // HEIC and TIFF are images that browsers largely will not render, so
  // they are listed as files rather than shown as a broken picture.
  if (type === 'image/heic' || type === 'image/heif' || type === 'image/tiff') return false;
  return type.startsWith('image/');
}

/**
 * A filename safe to put in a storage key, with its extension kept.
 *
 * Keeps the extension because the signed URL's download name comes from
 * the path, and a plat that arrives as "plat" instead of "plat.pdf"
 * opens in nothing.
 */
export function safeAttachmentName(name, fallback = 'file') {
  const raw = String(name || '').trim() || fallback;
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_{2,}/g, '_').replace(/^[._-]+/, '');
  const base = cleaned || fallback;
  const dot = base.lastIndexOf('.');
  const stem = (dot > 0 ? base.slice(0, dot) : base).slice(0, 60) || fallback;
  const ext = dot > 0 ? base.slice(dot).slice(0, 12).toLowerCase() : '';
  return `${stem}${ext}`;
}

/**
 * The name shown in the thread.
 *
 * Looser than safeAttachmentName, because this one is never a storage
 * key: it is text in a bubble, so "Plat — Section 14 (R).pdf" should
 * read that way rather than as Plat_Section_14_R_.pdf.
 *
 * What it must not keep is anything that makes it look like a path.
 * The request supplies this string, and an earlier version of
 * validateAttachment passed it through with only a trim — so a caller
 * could label an attachment "../../Settlement Statement.pdf" and have
 * the thread display exactly that. Separators, traversal, control
 * characters and runaway length all go.
 *
 * Note what this does NOT try to do: stop somebody naming a screenshot
 * "Settlement Statement.pdf". They can name the real file that before
 * uploading it, so it is not a property this layer can enforce — the
 * mime type is carried separately and is what decides how the file is
 * rendered and served.
 */
export function safeDisplayName(name, fallback = 'file') {
  const raw = String(name || '');
  const cleaned = raw
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\/]/g, ' ')
    .replace(/\.{2,}/g, '.')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[.\s]+/, '');
  return cleaned.slice(0, 120) || fallback;
}

/**
 * A clipboard paste has no filename at all — the browser hands over a
 * blob called "image.png" at best and "" at worst. Naming it by when
 * it was pasted is more use in a thread than "image".
 */
export function pastedAttachmentName(mime, at = new Date()) {
  const ext = {
    'image/png': '.png', 'image/jpeg': '.jpg', 'image/jpg': '.jpg',
    'image/webp': '.webp', 'image/gif': '.gif',
  }[String(mime || '').toLowerCase()] || '.png';
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
    + `-${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `screenshot-${stamp}${ext}`;
}

/** The group id a single send's files share. */
export function attachmentGroupId() {
  return `cat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const GROUP_RE = /^[A-Za-z0-9_-]{1,64}$/;
const DEAL_RE = /^[A-Za-z0-9_-]{1,128}$/;
const TENANT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Where a chat attachment lives.
 *
 *   tenants/<tenantId>/chat/<dealId>/<groupId>/<name>
 *
 * Under tenants/ because that is the only prefix the bucket's insert
 * policy admits (storage_path_tenant_id(name) must equal your tenant —
 * see migration-259). Scoped by deal so the route can rebuild the
 * expected prefix from a deal it has already authorised, and so a stray
 * object cannot be attached across deals.
 *
 * Returns null rather than a half-built path when any part is unusable:
 * a caller that ignores the null gets an upload refused by the bucket
 * rather than a file in the wrong place.
 */
export function chatAttachmentPath({ tenantId, dealId, groupId, name }) {
  const tenant = String(tenantId || '').trim();
  const deal = String(dealId || '').trim();
  const group = String(groupId || '').trim();
  if (!TENANT_RE.test(tenant)) return null;
  if (!DEAL_RE.test(deal)) return null;
  if (!GROUP_RE.test(group)) return null;
  const safe = safeAttachmentName(name);
  if (!safe) return null;
  return `tenants/${tenant}/chat/${deal}/${group}/${safe}`;
}

/**
 * Take one apart, strictly.
 *
 * Strict because this is the half that runs on the server. Anything
 * unexpected — a different prefix, a traversal segment, a missing
 * piece, an extra directory — returns null and the attachment is
 * refused. Six segments exactly, and no segment may be `.` or `..`.
 */
export function parseChatAttachmentPath(storagePath) {
  const path = String(storagePath || '').trim();
  if (!path || path.startsWith('/') || path.includes('\\')) return null;
  // A traversal or an empty segment anywhere disqualifies it outright,
  // before any part is read as meaningful.
  //
  // Belt and braces, and known to be: the three regexes below reject
  // dots outright, and the name has to round-trip through
  // safeAttachmentName, so every `.`/`..` segment is already refused by
  // one of them. Deleting this line changes no outcome — a mutation run
  // confirmed that. It stays because it says the rule out loud at the
  // top, where someone loosening a regex will read it.
  const parts = path.split('/');
  if (parts.length !== 6) return null;
  if (parts.some((part) => !part || part === '.' || part === '..')) return null;
  const [root, tenant, kind, deal, group, name] = parts;
  if (root !== 'tenants' || kind !== 'chat') return null;
  if (!TENANT_RE.test(tenant)) return null;
  if (!DEAL_RE.test(deal)) return null;
  if (!GROUP_RE.test(group)) return null;
  // The name must be exactly what safeAttachmentName would have
  // produced, so a path cannot carry characters the builder would have
  // stripped.
  if (name !== safeAttachmentName(name)) return null;
  return { tenantId: tenant, dealId: deal, groupId: group, name };
}

/**
 * Does this path belong to this caller, on this deal?
 *
 * The check the route makes. Rebuilt from the caller's own verified
 * tenant and the deal the route has already authorised — never from
 * anything in the request body.
 */
export function attachmentPathBelongsTo(storagePath, { tenantId, dealId }) {
  const parsed = parseChatAttachmentPath(storagePath);
  if (!parsed) return false;
  if (!TENANT_RE.test(String(tenantId || ''))) return false;
  return parsed.tenantId.toLowerCase() === String(tenantId).toLowerCase()
    && parsed.dealId === String(dealId || '');
}

/**
 * Validate one attachment as described by the client.
 *
 * @returns {{ ok: true, value: object } | { ok: false, reason: string }}
 */
export function validateAttachment(input, { tenantId, dealId } = {}) {
  const storagePath = String(input?.storagePath || '').trim();
  if (!attachmentPathBelongsTo(storagePath, { tenantId, dealId })) {
    return { ok: false, reason: 'That file is not on this deal.' };
  }
  const mime = String(input?.mime || '').toLowerCase().trim();
  if (!ALLOWED_ATTACHMENT_MIME.has(mime)) {
    return { ok: false, reason: `Unsupported file type: ${mime || 'unknown'}.` };
  }
  const size = Number(input?.size);
  if (!Number.isFinite(size) || size <= 0) return { ok: false, reason: 'That file is empty.' };
  if (size > MAX_ATTACHMENT_BYTES) {
    return { ok: false, reason: `That file is ${(size / 1024 / 1024).toFixed(1)} MB. The limit is 25 MB.` };
  }
  const parsed = parseChatAttachmentPath(storagePath);
  return {
    ok: true,
    value: {
      storagePath,
      // Sanitised, not trusted. This string comes from the request and
      // is displayed in the thread, so it may keep spaces and ordinary
      // punctuation but never a separator or a traversal — see
      // safeDisplayName. Falls back to the name in the PATH, which the
      // route rebuilt and verified.
      name: safeDisplayName(input?.name, parsed.name),
      mime,
      size: Math.round(size),
    },
  };
}

/** Human size, for the chip under a file. */
export function formatAttachmentSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
